import React from "react";
import { Helmet } from "react-helmet";
import msiProducts from "../../../data/msi"; // same structure as lenovo.js

export default function MSI() {
  const pageTitle =
    "Certified Refurbished MSI Laptops in Bangalore | Credible Technologies";
  const pageDescription =
    "Buy certified refurbished MSI laptops with warranty, tested for performance and reliability. Ideal for gamers, creators, and power users in Bangalore.";

  return (
    <main className="w-full bg-white text-gray-900">

      {/* =================== SEO HEAD =================== */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href="https://www.credibletech.in/refurbished-laptops/msi/laptops"
        />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.credibletech.in/refurbished-laptops/msi/laptops"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      {/* =====================================================
          MSI BRAND DESCRIPTION
      ===================================================== */}
      <section className="relative bg-gradient-to-br from-black via-slate-900 to-black text-white overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto px-4 py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm tracking-wide mb-6">
            🎮 MSI Refurbished Experts
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6">
            MSI Laptops – Refurbished & Certified
          </h1>

          <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-4">
            MSI is known for high-performance gaming and creator laptops, built with advanced GPUs, cooling solutions, and cutting-edge displays.
          </p>

          <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-4">
            Our refurbished MSI laptops go through strict quality checks, hardware diagnostics, and professional refurbishment to ensure reliable performance and long-term durability.
          </p>

          <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
            Whether you’re a gamer, content creator, or power user, our certified refurbished MSI laptops deliver smooth performance, excellent battery life, and complete peace of mind with warranty support.
          </p>
        </div>
      </section>

      {/* =====================================================
          MSI PRODUCTS SECTION
      ===================================================== */}
      <section id="msi-products" className="max-w-7xl mx-auto px-4 py-20">

        {/* Section Header */}
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
            Available MSI Laptops
          </h2>
          <p className="mt-4 text-gray-600 max-w-3xl mx-auto">
            Browse our collection of certified refurbished MSI laptops with verified specifications, warranty support, and excellent value for money.
          </p>
        </div>

        {/* Products List */}
        <div className="grid grid-cols-1 gap-10">
          {msiProducts.map((product) => (
            <article
              key={product.id}
              className="flex flex-col sm:flex-row border border-gray-200 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition bg-white"
            >
              {/* LEFT IMAGE */}
              <img
                src={product.image}
                alt={product.name}
                className="w-full sm:w-1/3 h-60 sm:h-auto object-cover"
              />

              {/* RIGHT CONTENT */}
              <div className="p-6 flex flex-col justify-between flex-grow bg-gradient-to-br from-gray-50 to-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-5">
                  {product.name}
                </h3>

                {/* Vertical Specs */}
                <ul className="flex flex-col gap-3 mb-6">
                  {product.specs.map((spec, index) => {
                    const colors = [
                      "bg-red-100 text-red-800",
                      "bg-green-100 text-green-800",
                      "bg-blue-100 text-blue-800",
                      "bg-yellow-100 text-yellow-800",
                      "bg-purple-100 text-purple-800",
                      "bg-pink-100 text-pink-800",
                      "bg-indigo-100 text-indigo-800",
                      "bg-teal-100 text-teal-800",
                    ];
                    const colorClass = colors[index % colors.length];

                    return (
                      <li
                        key={index}
                        className={`${colorClass} font-semibold px-4 py-2 rounded-full shadow-md hover:scale-105 transition-transform`}
                      >
                        {spec}
                      </li>
                    );
                  })}
                </ul>

                {/* WhatsApp CTA */}
                <a
                  href={`https://wa.me/919538577199?text=${encodeURIComponent(
                    product.whatsappText
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-auto bg-black text-white py-3 px-8 rounded-xl text-sm font-semibold hover:bg-gray-800 transition self-start"
                >
                  Buy Now
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
